import { useSyncExternalStore } from "react";
import {
  getDamageUploadRecord,
  getDamageUploadRecordsByOwner,
  markDamageUploadDeleting,
  putDamageUploadRecordIfCurrent,
  restoreDamageUploadAfterDeleteFailure,
  tombstoneDamageUploadRecord,
  updateDamageUploadRecord,
} from "./damageUploadStore.js";
import { fetchWithCustomerRequestAuth } from "../../../../../helpers/customerRequestAuth.js";
import { requestImageUploadPermit } from "./uploadConcurrencyGate.js";

const MAX_PARALLEL_UPLOADS = 1;
const MIN_UPLOAD_TIMEOUT_MS = 5 * 60_000;
const MAX_UPLOAD_TIMEOUT_MS = 15 * 60_000;
const MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND = 4 * 1024;
const UPLOAD_TIMEOUT_OVERHEAD_MS = 60_000;
const RETRY_DELAYS_MS = [3000, 10000, 30000, 60000];
const HIDDEN_STATES = new Set(["confirmed", "deleted", "superseded"]);
const JOB_STOP_STATES = new Set(["deleting", ...HIDDEN_STATES]);
const VISIBLE_STATES = new Set([
  "pending",
  "committed",
  "deleting",
]);
const EMPTY_SNAPSHOT = Object.freeze({});
const EMPTY_STATUS = Object.freeze({
  hydrated: false,
  ownerEmail: null,
  persistenceAvailable: true,
  storageDefinitelyUnavailable: false,
});

const listeners = new Set();
const records = new Map();
const jobs = new Map();
const retryTimers = new Map();
const ownedPreviewUrls = new Map();
const queue = [];

let snapshot = Object.freeze({});
let registryStatus = EMPTY_STATUS;
let apiBaseUrl = "";
let currentOwnerEmail = null;
let hydrationPromise = null;
let hydrationGeneration = 0;
let activeUploads = 0;
let onlineListenerInstalled = false;
let uploadTimeoutOverrideMs = null;
let persistConfirmationRecord = putDamageUploadRecordIfCurrent;

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function makeDamageUploadKey(vehicleId, category, step) {
  return `${String(vehicleId || "")}::damage::${String(
    category || ""
  )}::${String(step || "")}`;
}

function emit() {
  listeners.forEach((listener) => listener());
}

function publishEntry(key, entry) {
  snapshot = Object.freeze({ ...snapshot, [key]: Object.freeze(entry) });
  emit();
}

function removeEntry(key) {
  if (!snapshot[key]) return;
  const next = { ...snapshot };
  delete next[key];
  snapshot = Object.freeze(next);
  emit();
}

function publishStatus(patch) {
  registryStatus = Object.freeze({ ...registryStatus, ...patch });
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDamageUploadSnapshot() {
  return snapshot;
}

export function getDamageUploadRegistryStatus() {
  return registryStatus;
}

export function useDamageUploadSnapshot() {
  return useSyncExternalStore(
    subscribe,
    getDamageUploadSnapshot,
    () => EMPTY_SNAPSHOT
  );
}

export function useDamageUploadRegistryStatus() {
  return useSyncExternalStore(
    subscribe,
    getDamageUploadRegistryStatus,
    () => EMPTY_STATUS
  );
}

function createUploadId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

function createDeferred() {
  let resolve;
  const promise = new Promise((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve, settled: false };
}

function settle(job, result) {
  if (job.completion.settled) return;
  job.completion.settled = true;
  job.completion.resolve(result);
}

function entryFromRecord(record, previewUrl = null) {
  return {
    key: record.key,
    ownerEmail: record.ownerEmail,
    vehicleId: record.vehicleId,
    category: record.category,
    step: record.step,
    fileName: record.fileName || null,
    uploadId: record.uploadId,
    capturedAt: record.capturedAt,
    observedUploadId: record.observedUploadId ?? null,
    observedGeneration: record.observedGeneration ?? null,
    observedBaseMainUrl: record.observedBaseMainUrl ?? null,
    generation: record.generation || null,
    state: record.state,
    phase: record.phase || null,
    attempts: Number(record.attempts || 0),
    previewUrl: previewUrl || record.previewUrl || null,
    hasLocalBlob: Boolean(record.blob),
    imageUrl: record.imageUrl || null,
    locationImageUrl: record.locationImageUrl || null,
    previousImageUrl: record.previousImageUrl || null,
    hasLocationImage: record.hasLocationImage === true,
    size: record.size || null,
    serverRequestStarted: record.serverRequestStarted === true,
    persisted: record.persisted !== false,
    retryable: record.retryable !== false,
    errorName: record.errorName || null,
    errorCode: record.errorCode || null,
    errorMessage: record.errorMessage || null,
    httpStatus: Number(record.httpStatus || 0) || null,
    currentUploadId: record.currentUploadId || null,
    currentGeneration: record.currentGeneration || null,
  };
}

function createPreviewUrl(record) {
  if (!record?.blob || !globalThis.URL?.createObjectURL) return null;
  const previous = ownedPreviewUrls.get(record.key);
  if (previous) globalThis.URL.revokeObjectURL?.(previous);
  const next = globalThis.URL.createObjectURL(record.blob);
  ownedPreviewUrls.set(record.key, next);
  return next;
}

function releasePreviewUrl(key) {
  const previewUrl = ownedPreviewUrls.get(key);
  if (previewUrl) globalThis.URL?.revokeObjectURL?.(previewUrl);
  ownedPreviewUrls.delete(key);
}

function isJobCurrent(job) {
  return (
    jobs.get(job.key) === job &&
    records.get(job.key)?.uploadId === job.record.uploadId &&
    !JOB_STOP_STATES.has(records.get(job.key)?.state)
  );
}

function clearRetryTimer(key) {
  const timer = retryTimers.get(key);
  if (timer) clearTimeout(timer);
  retryTimers.delete(key);
}

function acquireUploadSlot(job) {
  if (!job) return Promise.resolve(false);
  if (job.slotHeld) {
    return job.uploadPermit?.promise || Promise.resolve(true);
  }
  job.slotHeld = true;
  activeUploads += 1;
  job.uploadPermit = requestImageUploadPermit(job);
  return job.uploadPermit.promise;
}

function releaseUploadSlot(job) {
  if (!job?.slotHeld) return false;
  job.slotHeld = false;
  const uploadPermit = job.uploadPermit;
  job.uploadPermit = null;
  uploadPermit?.cancel();
  activeUploads = Math.max(0, activeUploads - 1);
  return true;
}

function abortJob(job, result = { ok: false, superseded: true }) {
  if (!job) return;
  job.controller?.abort();
  job.cancelRequest?.();
  clearTimeout(job.timeoutId);
  job.timeoutId = null;
  clearRetryTimer(job.key);
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (queue[index] === job) queue.splice(index, 1);
  }
  settle(job, result);
  if (releaseUploadSlot(job)) scheduleQueue();
}

async function parseResponseBody(response) {
  const contentType = response.headers?.get?.("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => "");
  return text ? { error: text } : {};
}

function parseRetryAfter(response) {
  const value = response.headers?.get?.("retry-after");
  if (!value) return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
}

function createUploadError(response, result) {
  const error = new Error(
    result?.error ||
      result?.message ||
      "Schadenfoto konnte nicht hochgeladen werden."
  );
  const hasCurrentImageUrl =
    Object.prototype.hasOwnProperty.call(result || {}, "currentImageUrl") &&
    (result?.currentImageUrl === null ||
      typeof result?.currentImageUrl === "string");
  error.name = "DamageUploadError";
  error.status = response.status;
  error.code = result?.code || null;
  error.currentUploadId = result?.currentUploadId || null;
  error.currentGeneration = Number(result?.currentGeneration || 0) || null;
  error.currentStatus = result?.currentStatus || null;
  error.hasCurrentImageUrl = hasCurrentImageUrl;
  error.currentImageUrl = hasCurrentImageUrl
    ? typeof result.currentImageUrl === "string"
      ? result.currentImageUrl.trim() || null
      : null
    : undefined;
  error.externalReplacement = result?.externalReplacement === true;
  error.retryAfterMs = parseRetryAfter(response);
  error.retryable =
    response.status === 401 ||
    response.status === 408 ||
    response.status === 425 ||
    response.status === 429 ||
    response.status >= 500;
  return error;
}

function buildUploadForm(record) {
  const mainFile = new File(
    [record.blob],
    record.fileName || `${record.step}.jpg`,
    { type: record.contentType || record.blob?.type || "image/jpeg" }
  );
  const form = new FormData();
  form.append("file", mainFile);
  if (record.locationBlob) {
    const locationFile = new File(
      [record.locationBlob],
      record.locationFileName || `${record.step}-location.png`,
      {
        type:
          record.locationContentType ||
          record.locationBlob.type ||
          "image/png",
      }
    );
    form.append("locationFile", locationFile);
  }
  form.append("email", record.ownerEmail);
  form.append("id", record.vehicleId);
  form.append("type", record.step);
  form.append("category", record.category);
  if (record.size) form.append("size", record.size);
  form.append("imageSource", record.imageSource || "camera");
  form.append("uploadId", record.uploadId);
  form.append("capturedAt", String(record.capturedAt));
  if (record.observedResetIntentId !== undefined) {
    form.append(
      "observedResetIntentId",
      record.observedResetIntentId === null
        ? "null"
        : String(record.observedResetIntentId)
    );
  }
  if (record.generation) {
    form.append("uploadGeneration", String(record.generation));
  }
  return form;
}

function buildReserveBody(record) {
  const hasObservedServerState = [
    "observedUploadId",
    "observedGeneration",
    "observedBaseMainUrl",
  ].every((field) => Object.prototype.hasOwnProperty.call(record, field));
  return JSON.stringify({
    email: record.ownerEmail,
    id: record.vehicleId,
    type: record.step,
    category: record.category,
    size: record.size || null,
    hasLocationFile: record.hasLocationImage === true,
    imageSource: record.imageSource || "camera",
    uploadId: record.uploadId,
    capturedAt: record.capturedAt,
    ...(hasObservedServerState
      ? {
          observedUploadId: record.observedUploadId ?? null,
          observedGeneration: record.observedGeneration ?? null,
          observedBaseMainUrl: record.observedBaseMainUrl ?? null,
        }
      : {}),
    ...(record.observedResetIntentId !== undefined
      ? { observedResetIntentId: record.observedResetIntentId }
      : {}),
    ...(record.generation
      ? { uploadGeneration: Number(record.generation) }
      : {}),
  });
}

function getValidatedReservation(record, result) {
  if (result?.uploadId !== record.uploadId) {
    const error = new Error(
      "Der Server hat eine falsche Schadenfoto-Upload-ID reserviert."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  const generation = Number(result?.generation);
  if (!Number.isSafeInteger(generation) || generation <= 0) {
    const error = new Error(
      "Der Server hat keine gültige Schadenfoto-Generation reserviert."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  if (record.generation && Number(record.generation) !== generation) {
    const error = new Error(
      "Der Server hat eine andere Schadenfoto-Generation reserviert."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const status = String(result?.status || "");
  if (!["pending", "main_uploaded"].includes(status)) {
    const error = new Error(
      "Der Server hat den Schadenfoto-Upload nicht reserviert."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const imageUrl =
    typeof result?.file?.url === "string" ? result.file.url.trim() : "";
  const locationImageUrl =
    typeof result?.locationFile?.url === "string"
      ? result.locationFile.url.trim()
      : "";
  const isHttpUrl = (value) => {
    try {
      return ["http:", "https:"].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  };
  if (
    status === "main_uploaded" &&
    (!isHttpUrl(imageUrl) ||
      (record.hasLocationImage && !isHttpUrl(locationImageUrl)))
  ) {
    const error = new Error(
      "Der Server meldet einen fertigen Schadenfoto-Upload ohne alle Bild-URLs."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  return {
    generation,
    completed: status === "main_uploaded",
    imageUrl: imageUrl || null,
    locationImageUrl: locationImageUrl || null,
  };
}

async function reserveRecord(record, signal) {
  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    ""
  );
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Schadenfoto-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/car-damage/upload/reserve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: buildReserveBody(record),
      signal,
    }
  );
  const result = await parseResponseBody(response);
  if (!response.ok) throw createUploadError(response, result);
  return getValidatedReservation(record, result);
}

async function uploadRecord(record, signal) {
  if (!record.blob) {
    const error = new Error("Das lokal gespeicherte Schadenfoto fehlt.");
    error.retryable = false;
    throw error;
  }
  if (record.hasLocationImage && !record.locationBlob) {
    const error = new Error("Die lokale Schadensmarkierung fehlt.");
    error.retryable = false;
    throw error;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const error = new Error("Keine Internetverbindung.");
    error.name = "OfflineDamageUploadError";
    error.retryable = true;
    throw error;
  }

  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    ""
  );
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Schadenfoto-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/car-damage/upload`,
    {
      method: "POST",
      body: buildUploadForm(record),
      signal,
    }
  );
  const result = await parseResponseBody(response);
  if (!response.ok) throw createUploadError(response, result);

  const imageUrl =
    typeof result?.file?.url === "string" ? result.file.url.trim() : "";
  const locationImageUrl =
    typeof result?.locationFile?.url === "string"
      ? result.locationFile.url.trim()
      : "";
  const isHttpUrl = (value) => {
    try {
      return ["http:", "https:"].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  };
  if (
    !isHttpUrl(imageUrl) ||
    (record.hasLocationImage && !isHttpUrl(locationImageUrl))
  ) {
    const error = new Error(
      "Upload erfolgreich, aber der Server hat nicht alle Bild-URLs bestätigt."
    );
    error.name = "DamageUploadProtocolError";
    // Der atomare Server-Commit kann bereits erfolgt sein, obwohl die
    // Response beschädigt ist. Dieselbe uploadId darf sicher erneut laufen.
    error.retryable = true;
    throw error;
  }
  if (result?.uploadId !== record.uploadId) {
    const error = new Error("Der Server hat eine falsche Upload-ID bestätigt.");
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  const generation = Number(result?.generation);
  if (!Number.isSafeInteger(generation) || generation <= 0) {
    const error = new Error(
      "Der Server hat keine gültige Upload-Generation bestätigt."
    );
    error.name = "DamageUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  return {
    imageUrl,
    locationImageUrl: locationImageUrl || null,
    generation,
  };
}

function getUploadTimeoutMs(record) {
  if (uploadTimeoutOverrideMs !== null) return uploadTimeoutOverrideMs;
  const totalSize =
    Math.max(0, Number(record?.blob?.size || 0)) +
    Math.max(0, Number(record?.locationBlob?.size || 0));
  const sizeAwareTimeout =
    totalSize > 0
      ? Math.ceil(
          (totalSize / MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND) * 1000
        ) + UPLOAD_TIMEOUT_OVERHEAD_MS
      : 0;
  return Math.min(
    MAX_UPLOAD_TIMEOUT_MS,
    Math.max(MIN_UPLOAD_TIMEOUT_MS, sizeAwareTimeout)
  );
}

export function __getDamageUploadTimeoutForTests(record) {
  return getUploadTimeoutMs(record);
}

function uploadRecordWithDeadline(job, record) {
  const timeoutMs = getUploadTimeoutMs(record);
  return new Promise((resolve, reject) => {
    let requestSettled = false;
    const finish = (handler, value) => {
      if (requestSettled) return;
      requestSettled = true;
      clearTimeout(job.timeoutId);
      job.timeoutId = null;
      job.cancelRequest = null;
      handler(value);
    };

    job.timeoutId = setTimeout(() => {
      if (requestSettled) return;
      job.timedOut = true;
      job.controller?.abort();
      const error = new Error(
        "Der Schadenfoto-Upload hat zu lange gedauert."
      );
      error.name = "DamageUploadTimeoutError";
      error.retryable = true;
      finish(reject, error);
    }, timeoutMs);
    job.cancelRequest = () => {
      const error = new Error("Der Schadenfoto-Upload wurde abgebrochen.");
      error.name = "AbortError";
      error.retryable = false;
      finish(reject, error);
    };

    // WebKit kann fetch trotz AbortController offen lassen. Diese explizite
    // Deadline löst den Worker trotzdem und gibt den Queue-Slot frei.
    Promise.resolve()
      .then(async () => {
        const reservation = await reserveRecord(
          record,
          job.controller.signal
        );
        const reserved = await persistPatch(record, {
          generation: reservation.generation,
          phase: reservation.completed ? "committing" : "uploading",
        });
        if (!reserved || !isJobCurrent(job)) {
          const error = new Error(
            "Der Schadenfoto-Upload wurde durch einen neueren Upload ersetzt."
          );
          error.name = "DamageUploadPersistenceCasError";
          error.retryable = false;
          throw error;
        }
        publishEntry(
          record.key,
          entryFromRecord(record, snapshot[record.key]?.previewUrl)
        );
        if (reservation.completed) {
          return {
            imageUrl: reservation.imageUrl,
            locationImageUrl: reservation.locationImageUrl,
            generation: reservation.generation,
          };
        }
        return uploadRecord(record, job.controller.signal);
      })
      .then(
        (result) => finish(resolve, result),
        (error) => finish(reject, error)
      );
  });
}

async function persistPatch(record, patch) {
  const updatedAt = Date.now();
  if (record.persisted === false) {
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  }

  try {
    const updated = await updateDamageUploadRecord(
      record.key,
      record.uploadId,
      { ...patch, updatedAt }
    );
    if (!updated) return false;
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  } catch (error) {
    console.error(
      "Schadenfoto-Uploadstatus konnte nicht gespeichert werden:",
      error
    );
    publishStatus({ persistenceAvailable: false });
    Object.assign(record, patch, { updatedAt, persisted: false });
    records.set(record.key, record);
    return true;
  }
}

function adoptStoredRecord(storedRecord) {
  if (!storedRecord || storedRecord.ownerEmail !== currentOwnerEmail) {
    return false;
  }

  const memoryRecord = records.get(storedRecord.key);
  if (
    memoryRecord?.uploadId === storedRecord.uploadId &&
    Number(memoryRecord.updatedAt || 0) > Number(storedRecord.updatedAt || 0)
  ) {
    return false;
  }
  if (memoryRecord && memoryRecord.uploadId !== storedRecord.uploadId) {
    const storedCapturedAt = Number(storedRecord.capturedAt || 0);
    const memoryCapturedAt = Number(memoryRecord.capturedAt || 0);
    if (
      storedCapturedAt < memoryCapturedAt ||
      (storedCapturedAt === memoryCapturedAt &&
        String(storedRecord.uploadId) < String(memoryRecord.uploadId))
    ) {
      return false;
    }
  }

  const existingJob = jobs.get(storedRecord.key);
  if (
    existingJob?.record?.uploadId === storedRecord.uploadId &&
    storedRecord.state === "pending"
  ) {
    return true;
  }
  if (existingJob) {
    abortJob(existingJob);
    if (jobs.get(storedRecord.key) === existingJob) {
      jobs.delete(storedRecord.key);
    }
  }

  const nextRecord = { ...storedRecord, persisted: true };
  records.set(nextRecord.key, nextRecord);
  if (HIDDEN_STATES.has(nextRecord.state)) {
    releasePreviewUrl(nextRecord.key);
    removeEntry(nextRecord.key);
    return true;
  }
  const previewUrl = createPreviewUrl(nextRecord);
  publishEntry(nextRecord.key, entryFromRecord(nextRecord, previewUrl));
  if (nextRecord.state === "pending") enqueueJob(createJob(nextRecord));
  return true;
}

async function reconcileAfterCasLoss(job) {
  clearRetryTimer(job.key);
  settle(job, { ok: false, superseded: true });
  if (jobs.get(job.key) === job) jobs.delete(job.key);

  if (records.get(job.key)?.uploadId === job.record.uploadId) {
    records.delete(job.key);
    releasePreviewUrl(job.key);
    removeEntry(job.key);
  }

  try {
    adoptStoredRecord(await getDamageUploadRecord(job.key));
  } catch (error) {
    console.error("Neuerer Schadenfoto-Upload konnte nicht geladen werden:", error);
  }
}

function retryDelay(record, error) {
  const index = Math.min(
    Number(record.attempts || 0),
    RETRY_DELAYS_MS.length - 1
  );
  return Math.max(RETRY_DELAYS_MS[index], Number(error?.retryAfterMs || 0));
}

async function scheduleRetry(job, error, rebasePatch = null) {
  if (!isJobCurrent(job)) return;
  const record = job.record;
  const delay = retryDelay(record, error);
  const persisted = await persistPatch(record, {
    ...(rebasePatch || {}),
    state: "pending",
    phase: "retry_wait",
    attempts: Number(record.attempts || 0) + 1,
    retryable: true,
    errorName: error?.name || null,
    errorCode: error?.code || null,
    errorMessage: error?.message || "Upload wird erneut versucht.",
    httpStatus: Number(error?.status || 0) || null,
    nextRetryAt: Date.now() + delay,
  });
  if (!persisted) {
    await reconcileAfterCasLoss(job);
    return;
  }
  if (!isJobCurrent(job)) return;
  publishEntry(
    record.key,
    entryFromRecord(record, snapshot[record.key]?.previewUrl)
  );
  clearRetryTimer(record.key);
  retryTimers.set(
    record.key,
    setTimeout(() => {
      retryTimers.delete(record.key);
      if (isJobCurrent(job)) enqueueJob(job);
    }, delay)
  );
}

function isStaleFromLocalPredecessor(record, error) {
  if (
    Number(record?.generation || 0) > 0 ||
    !record?.blob ||
    !record?.localPredecessorUploadId ||
    record.localPredecessorUploadId !== error?.currentUploadId ||
    !Number.isSafeInteger(Number(error?.currentGeneration)) ||
    Number(error.currentGeneration) <= 0 ||
    !["pending", "main_uploaded"].includes(error?.currentStatus) ||
    error?.externalReplacement === true
  ) {
    return false;
  }

  const knownGeneration = Number(record.localPredecessorGeneration || 0);
  return (
    knownGeneration <= 0 ||
    knownGeneration === Number(error.currentGeneration)
  );
}

function getLocalPredecessorRebasePatch(record, error) {
  if (
    !isStaleFromLocalPredecessor(record, error) ||
    error?.hasCurrentImageUrl !== true
  ) {
    return null;
  }

  const currentImageUrl = error.currentImageUrl ?? null;
  const alreadyObserved =
    record.observedUploadId === error.currentUploadId &&
    Number(record.observedGeneration || 0) ===
      Number(error.currentGeneration) &&
    (record.observedBaseMainUrl || null) === currentImageUrl;
  if (alreadyObserved) return null;

  return {
    generation: null,
    localPredecessorGeneration: Number(error.currentGeneration),
    observedUploadId: error.currentUploadId,
    observedGeneration: Number(error.currentGeneration),
    observedBaseMainUrl: currentImageUrl,
    previousImageUrl: currentImageUrl,
  };
}

async function runJob(job) {
  if (!isJobCurrent(job) || job.running) return;
  job.running = true;
  const uploadPermitGranted = acquireUploadSlot(job);
  const record = job.record;

  try {
    if (!(await uploadPermitGranted) || !isJobCurrent(job)) return;
    job.controller = new AbortController();
    job.timedOut = false;

    const canStart = await persistPatch(record, {
      state: "pending",
      phase: "reserving",
      serverRequestStarted: true,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
    });
    if (!canStart || !isJobCurrent(job)) {
      if (!canStart) await reconcileAfterCasLoss(job);
      else settle(job, { ok: false, superseded: true });
      return;
    }
    publishEntry(
      record.key,
      entryFromRecord(record, snapshot[record.key]?.previewUrl)
    );

    const result = await uploadRecordWithDeadline(job, record);
    if (releaseUploadSlot(job)) scheduleQueue();
    if (!isJobCurrent(job)) return;

    const committed = await persistPatch(record, {
      state: "committed",
      phase: null,
      blob: null,
      locationBlob: null,
      imageUrl: result.imageUrl,
      locationImageUrl: result.locationImageUrl,
      generation: result.generation,
      retryable: false,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
      nextRetryAt: null,
    });
    if (!committed) {
      await reconcileAfterCasLoss(job);
      return;
    }
    if (!isJobCurrent(job)) return;
    publishEntry(
      record.key,
      entryFromRecord(record, snapshot[record.key]?.previewUrl)
    );
    settle(job, { ok: true, ...result });
    if (jobs.get(job.key) === job) jobs.delete(job.key);
  } catch (error) {
    if (releaseUploadSlot(job)) scheduleQueue();
    if (!isJobCurrent(job)) return;
    if (error?.name === "DamageUploadPersistenceCasError") {
      await reconcileAfterCasLoss(job);
    } else if (error?.name === "DamageUploadTimeoutError") {
      await scheduleRetry(job, error);
    } else if (error?.name === "AbortError") {
      settle(job, { ok: false, superseded: true });
    } else if (
      error?.status === 409 &&
      error?.code === "STALE_PHOTO_UPLOAD"
    ) {
      const predecessorRebasePatch = getLocalPredecessorRebasePatch(
        record,
        error
      );
      if (predecessorRebasePatch) {
        await scheduleRetry(job, error, predecessorRebasePatch);
        return;
      }
      // Preserve the latest retake during a rolling backend deployment where
      // an older 409 response does not yet expose currentImageUrl.
      if (
        isStaleFromLocalPredecessor(record, error) &&
        error?.hasCurrentImageUrl !== true
      ) {
        await scheduleRetry(job, error);
        return;
      }
      const nextState =
        error.currentStatus === "deleted" ? "deleted" : "superseded";
      const superseded = await persistPatch(record, {
        state: nextState,
        phase: null,
        blob: null,
        locationBlob: null,
        retryable: false,
        currentUploadId: error.currentUploadId,
        currentGeneration: error.currentGeneration,
        errorCode: error.code,
        errorMessage: null,
      });
      if (!superseded) {
        await reconcileAfterCasLoss(job);
        return;
      }
      releasePreviewUrl(record.key);
      removeEntry(record.key);
      settle(job, {
        ok: false,
        deleted: nextState === "deleted",
        superseded: nextState !== "deleted",
        externalReplacement: error.externalReplacement,
      });
      if (jobs.get(job.key) === job) jobs.delete(job.key);
    } else if (error?.retryable !== false) {
      await scheduleRetry(job, error);
    } else {
      const failed = await persistPatch(record, {
        state: "failed",
        phase: null,
        retryable: false,
        errorName: error?.name || null,
        errorCode: error?.code || null,
        errorMessage:
          error?.message || "Schadenfoto konnte nicht hochgeladen werden.",
        httpStatus: Number(error?.status || 0) || null,
      });
      if (!failed) {
        await reconcileAfterCasLoss(job);
        return;
      }
      publishEntry(
        record.key,
        entryFromRecord(record, snapshot[record.key]?.previewUrl)
      );
      settle(job, { ok: false, error });
      if (jobs.get(job.key) === job) jobs.delete(job.key);
    }
  } finally {
    clearTimeout(job.timeoutId);
    job.timeoutId = null;
    job.controller = null;
    job.cancelRequest = null;
    job.timedOut = false;
    job.running = false;
    releaseUploadSlot(job);
    scheduleQueue();
  }
}

function scheduleQueue() {
  while (activeUploads < MAX_PARALLEL_UPLOADS && queue.length) {
    const job = queue.shift();
    if (!isJobCurrent(job) || job.running) continue;
    void runJob(job);
  }
}

function enqueueJob(job) {
  if (!isJobCurrent(job) || job.running || queue.includes(job)) return;
  queue.push(job);
  scheduleQueue();
}

function createJob(record) {
  const job = {
    key: record.key,
    record,
    completion: createDeferred(),
    controller: null,
    cancelRequest: null,
    timeoutId: null,
    timedOut: false,
    running: false,
    slotHeld: false,
    uploadPermit: null,
  };
  jobs.set(record.key, job);
  return job;
}

export function enqueueDamageUpload({
  vehicleId,
  category,
  step,
  blob,
  locationBlob = null,
  size = null,
  previewUrl = null,
  ownerEmail,
  previousImageUrl = null,
  imageSource = "camera",
  fileName,
  contentType,
  locationFileName,
  locationContentType,
  observedResetIntentId,
  observedUploadId = null,
  observedGeneration = null,
  observedBaseMainUrl = null,
}) {
  if (!blob) throw new TypeError("enqueueDamageUpload benötigt einen Blob.");
  if (locationBlob && !size) {
    throw new TypeError(
      "enqueueDamageUpload benötigt bei einer Markierung auch die Schadensgröße."
    );
  }

  const normalizedOwner = normalizeEmail(ownerEmail);
  // A capture can happen before the global manager's initialization effect.
  // Claim the authenticated owner synchronously so initialization cannot reset
  // this fresh job and leave it waiting in IndexedDB until a later reload.
  if (!currentOwnerEmail && normalizedOwner) {
    currentOwnerEmail = normalizedOwner;
    publishStatus({ ownerEmail: normalizedOwner });
  }
  const key = makeDamageUploadKey(vehicleId, category, step);
  const previousRecord = records.get(key);
  const previousRecordGeneration = Number(previousRecord?.generation || 0);
  const localPredecessorUploadId =
    previousRecord?.state === "pending" &&
    previousRecord.serverRequestStarted === true
      ? previousRecord.uploadId
      : null;
  const localPredecessorGeneration =
    localPredecessorUploadId && previousRecordGeneration > 0
      ? previousRecordGeneration
      : null;
  const hasObservedPreviousRecord =
    previousRecord?.uploadId &&
    Number.isSafeInteger(previousRecordGeneration) &&
    previousRecordGeneration > 0;
  const effectiveObservedUploadId = hasObservedPreviousRecord
    ? previousRecord.uploadId
    : observedUploadId;
  const effectiveObservedGeneration = hasObservedPreviousRecord
    ? previousRecordGeneration
    : observedGeneration;
  const effectiveObservedBaseMainUrl = hasObservedPreviousRecord
    ? previousRecord.state === "committed"
      ? previousRecord.imageUrl || null
      : previousRecord.previousImageUrl || null
    : observedBaseMainUrl;
  const capturedAt = Math.max(
    Date.now(),
    Number(previousRecord?.capturedAt || 0) + 1
  );
  const record = {
    key,
    ownerEmail: normalizedOwner,
    vehicleId: String(vehicleId || ""),
    category: String(category || ""),
    step: String(step || ""),
    blob,
    locationBlob,
    hasLocationImage: Boolean(locationBlob),
    size: size || null,
    fileName: fileName || `${step}.jpg`,
    contentType: contentType || blob.type || "image/jpeg",
    locationFileName: locationFileName || `${step}-location.png`,
    locationContentType:
      locationContentType || locationBlob?.type || "image/png",
    ...(observedResetIntentId !== undefined
      ? { observedResetIntentId }
      : {}),
    imageSource,
    apiBaseUrl,
    uploadId: createUploadId(),
    capturedAt,
    generation: null,
    localPredecessorUploadId,
    localPredecessorGeneration,
    observedUploadId: effectiveObservedUploadId || null,
    observedGeneration:
      Number(effectiveObservedGeneration || 0) > 0
        ? Number(effectiveObservedGeneration)
        : null,
    observedBaseMainUrl: effectiveObservedBaseMainUrl || null,
    previousImageUrl,
    imageUrl: null,
    locationImageUrl: null,
    state: "pending",
    phase: "persisting",
    attempts: 0,
    nextRetryAt: null,
    serverRequestStarted: false,
    retryable: true,
    errorName: null,
    errorCode: null,
    errorMessage: null,
    httpStatus: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    persisted: true,
  };

  const oldJob = jobs.get(key);
  if (oldJob) abortJob(oldJob);
  clearRetryTimer(key);
  records.set(key, record);
  const job = createJob(record);
  const resolvedPreviewUrl = previewUrl || createPreviewUrl(record);
  publishEntry(key, entryFromRecord(record, resolvedPreviewUrl));

  const persisted = (async () => {
    try {
      if (!registryStatus.persistenceAvailable) {
        const error = new Error(
          "IndexedDB für Schadenfotos ist vorübergehend nicht verfügbar."
        );
        error.name = "DamageUploadStorageUnavailableError";
        throw error;
      }
      const didPersist = await putDamageUploadRecordIfCurrent(record);
      if (!isJobCurrent(job) || !didPersist) {
        if (!didPersist) await reconcileAfterCasLoss(job);
        else settle(job, { ok: false, superseded: true });
        return { ok: false, superseded: true };
      }
      record.phase = "queued";
      record.updatedAt = Date.now();
      publishEntry(key, entryFromRecord(record, resolvedPreviewUrl));
      enqueueJob(job);
      return { ok: true };
    } catch (error) {
      console.error(
        "Schadenfoto konnte nicht in IndexedDB gespeichert werden:",
        error
      );
      record.persisted = false;
      record.phase = "queued";
      publishStatus({ persistenceAvailable: false });
      publishEntry(key, entryFromRecord(record, resolvedPreviewUrl));
      enqueueJob(job);
      return { ok: false, error };
    }
  })();

  return {
    persisted,
    completion: job.completion.promise,
    previewUrl: resolvedPreviewUrl,
  };
}

export function getDamageUploadEntry(
  uploadSnapshot,
  { vehicleId, category = null, step }
) {
  if (category) {
    const direct =
      uploadSnapshot?.[makeDamageUploadKey(vehicleId, category, step)];
    if (direct) return direct;
  }
  return Object.values(uploadSnapshot || {}).find(
    (entry) =>
      String(entry?.vehicleId) === String(vehicleId) &&
      (!category || entry?.category === category) &&
      entry?.step === step
  );
}

export function mergePendingDamageUploadsIntoCarData(
  carData,
  { vehicleId, uploadSnapshot = snapshot } = {}
) {
  if (!carData) return carData;
  const entries = Object.values(uploadSnapshot).filter(
    (entry) =>
      String(entry?.vehicleId) === String(vehicleId) &&
      VISIBLE_STATES.has(entry?.state)
  );
  if (!entries.length) return carData;

  const carDamages = { ...(carData.carDamages || {}) };
  entries.forEach((entry) => {
    carDamages[entry.step] =
      entry.previewUrl || entry.imageUrl || carDamages[entry.step];
  });
  return { ...carData, carDamages };
}

export function isDamageUploadEntryServerConfirmed(entry, carData) {
  return Boolean(getDamageUploadEntryServerState(entry, carData));
}

function compareDamageUploadIdentity(entry, version) {
  const entryGeneration = Number(entry?.generation || 0);
  const serverGeneration = Number(version?.generation || 0);
  if (entryGeneration > 0) {
    return serverGeneration - entryGeneration;
  }

  const entryCapturedAt = Number(entry?.capturedAt || 0);
  const serverCapturedAt = Number(version?.capturedAt || 0);
  if (entryCapturedAt && serverCapturedAt && entryCapturedAt !== serverCapturedAt) {
    return serverCapturedAt - entryCapturedAt;
  }

  if (entryCapturedAt && serverCapturedAt) {
    return String(version?.uploadId || "").localeCompare(
      String(entry?.uploadId || "")
    );
  }
  return 0;
}

export function getDamageUploadEntryServerState(entry, carData) {
  if (!entry || !carData) return false;
  const version = carData?.photoUploadVersions?.carDamages?.[entry.step];
  const currentImageUrl = carData?.carDamages?.[entry.step] || null;
  const currentLocationUrl =
    carData?.carDamagesLocation?.[entry.step] || null;
  const entryGeneration = Number(entry.generation || 0);
  const serverGeneration = Number(version?.generation || 0);

  const exactMainCommit = Boolean(
    entry.uploadId &&
      version?.uploadId === entry.uploadId &&
      version?.status === "main_uploaded" &&
      version?.mainUrl &&
      currentImageUrl === version.mainUrl &&
      serverGeneration > 0 &&
      (!entryGeneration || entryGeneration === serverGeneration)
  );
  const exactLocationCommit =
    !entry.hasLocationImage ||
    Boolean(version?.locationUrl && currentLocationUrl === version.locationUrl);
  if (exactMainCommit && exactLocationCommit) {
    return {
      kind: "confirmed",
      imageUrl: version.mainUrl,
      locationImageUrl: version.locationUrl || null,
      uploadId: version.uploadId,
      generation: serverGeneration,
      status: version.status,
    };
  }

  if (
    version?.uploadId === entry.uploadId &&
    version?.status === "deleted" &&
    !currentImageUrl
  ) {
    return {
      kind: "deleted",
      imageUrl: null,
      locationImageUrl: null,
      uploadId: version.uploadId,
      generation: serverGeneration || null,
      status: version.status,
    };
  }

  const serverVersionIsNewer =
    version?.uploadId &&
    version.uploadId !== entry.uploadId &&
    compareDamageUploadIdentity(entry, version) > 0;
  const newerVersionIsConsistent = Boolean(
    (version?.status === "main_uploaded" &&
      version.mainUrl &&
      currentImageUrl === version.mainUrl) ||
      (version?.status === "pending" &&
        currentImageUrl === (version.baseMainUrl || null)) ||
      (version?.status === "deleted" && !currentImageUrl)
  );
  const externalMainReplacement = Boolean(
    version?.uploadId === entry.uploadId &&
      version?.status === "main_uploaded" &&
      version.mainUrl &&
      currentImageUrl &&
      currentImageUrl !== version.mainUrl
  );
  if (
    (serverVersionIsNewer && newerVersionIsConsistent) ||
    externalMainReplacement
  ) {
    return {
      kind: version?.status === "deleted" ? "deleted" : "replaced",
      imageUrl: currentImageUrl,
      locationImageUrl: currentLocationUrl,
      uploadId: version?.uploadId || null,
      generation: serverGeneration || null,
      status: version?.status || null,
    };
  }

  return null;
}

export async function acknowledgeDamageUploadServerConfirmation({
  vehicleId,
  category,
  step,
  uploadId,
  imageUrl = null,
  locationImageUrl = null,
  generation = null,
  serverUploadId = null,
  serverStatus = null,
  externalReplacement = false,
}) {
  const entry = getDamageUploadEntry(snapshot, {
    vehicleId,
    category,
    step,
  });
  if (!entry || !uploadId || entry.uploadId !== uploadId) return false;
  const key = entry.key;
  const record = records.get(key);
  if (
    !record ||
    record.uploadId !== uploadId ||
    record.state === "deleting"
  ) {
    return false;
  }

  const wasReplaced =
    externalReplacement ||
    Boolean(serverUploadId && serverUploadId !== uploadId) ||
    serverStatus === "deleted";
  const confirmedImageUrl =
    (typeof imageUrl === "string" && imageUrl.trim()) ||
    record.imageUrl ||
    null;
  const confirmedLocationImageUrl =
    (typeof locationImageUrl === "string" && locationImageUrl.trim()) ||
    record.locationImageUrl ||
    null;
  if (
    !wasReplaced &&
    (!confirmedImageUrl ||
      (record.hasLocationImage && !confirmedLocationImageUrl))
  ) {
    return false;
  }

  const terminalRecord = {
    ...record,
    blob: null,
    locationBlob: null,
    imageUrl: confirmedImageUrl,
    locationImageUrl: confirmedLocationImageUrl,
    generation: Number(generation || record.generation || 0) || null,
    state:
      serverStatus === "deleted"
        ? "deleted"
        : wasReplaced
          ? "superseded"
          : "confirmed",
    phase: null,
    retryable: false,
    errorCode: null,
    errorMessage: null,
    nextRetryAt: null,
    updatedAt: Date.now(),
  };

  // Firestore ist die Source of Truth. Im RAM wird ohne vorheriges IDB-await
  // terminalisiert, damit ein hängender Private-Mode-Speicher weder UI noch
  // Completion blockieren kann. Ein bereits existierender Retake wurde oben
  // anhand seiner anderen uploadId abgewiesen; ein danach gestarteter Retake
  // kann durch das IDB-CAS unten nicht überschrieben werden.
  records.set(key, terminalRecord);
  const activeJob = jobs.get(key);
  if (activeJob?.record?.uploadId === uploadId) {
    abortJob(
      activeJob,
      wasReplaced
        ? {
            ok: false,
            deleted: serverStatus === "deleted",
            externalReplacement: serverStatus !== "deleted",
            serverConfirmed: true,
          }
        : {
            ok: true,
            imageUrl: terminalRecord.imageUrl,
            locationImageUrl: terminalRecord.locationImageUrl,
            generation: terminalRecord.generation,
            serverConfirmed: true,
          }
    );
    if (jobs.get(key) === activeJob) jobs.delete(key);
  }
  clearRetryTimer(key);
  releasePreviewUrl(key);
  removeEntry(key);

  if (record.persisted !== false) {
    void Promise.resolve()
      .then(() => persistConfirmationRecord(terminalRecord))
      .then((persisted) => {
        if (!persisted) return;
        const current = records.get(key);
        if (current?.uploadId === uploadId && current.state === "confirmed") {
          current.persisted = true;
        }
      })
      .catch((error) => {
        console.error(
          "Schadenfoto-Bestätigung konnte nicht lokal gespeichert werden:",
          error
        );
        publishStatus({ persistenceAvailable: false });
      });
  }
  return true;
}

export async function prepareDamageUploadDelete({
  vehicleId,
  category = null,
  step,
  expectedImageUrl = null,
}) {
  const entry = getDamageUploadEntry(snapshot, {
    vehicleId,
    category,
    step,
  });
  if (!entry) {
    return {
      found: false,
      localOnly: false,
      expectedImageUrl,
      localAttemptGuarded: Boolean(expectedImageUrl),
    };
  }

  const key = entry.key;
  const memoryRecord = records.get(key) || { ...entry, key };
  let deletingRecord = memoryRecord;

  if (memoryRecord.persisted !== false) {
    try {
      const marked = await markDamageUploadDeleting(key, entry.uploadId);
      if (!marked.ok) {
        adoptStoredRecord(marked.current);
        return {
          found: false,
          conflict: true,
          localOnly: false,
          expectedImageUrl,
          localAttemptGuarded: true,
        };
      }
      deletingRecord = { ...marked.record, persisted: true };
    } catch (error) {
      console.error(
        "Schadenfoto-Löschung konnte nicht vorbereitet werden:",
        error
      );
      publishStatus({ persistenceAvailable: false });
      deletingRecord = {
        ...memoryRecord,
        state: "deleting",
        phase: null,
        persisted: false,
      };
    }
  } else {
    deletingRecord = {
      ...memoryRecord,
      state: "deleting",
      phase: null,
    };
  }

  const job = jobs.get(key);
  if (job) abortJob(job, { ok: false, deleted: true });
  if (jobs.get(key) === job) jobs.delete(key);
  clearRetryTimer(key);
  records.set(key, deletingRecord);
  publishEntry(
    key,
    entryFromRecord(deletingRecord, snapshot[key]?.previewUrl || null)
  );

  return {
    found: true,
    category: entry.category,
    localOnly:
      deletingRecord.serverRequestStarted !== true &&
      !deletingRecord.imageUrl &&
      !deletingRecord.previousImageUrl,
    expectedUploadId: deletingRecord.uploadId || null,
    expectedGeneration: deletingRecord.generation || null,
    expectedCapturedAt: deletingRecord.capturedAt || null,
    expectedImageUrl:
      deletingRecord.imageUrl ||
      deletingRecord.previousImageUrl ||
      expectedImageUrl ||
      null,
    localAttemptGuarded: true,
  };
}

export async function confirmDamageUploadDelete({
  vehicleId,
  category = null,
  step,
  uploadId = null,
  serverConfirmed = false,
}) {
  const entry = getDamageUploadEntry(snapshot, {
    vehicleId,
    category,
    step,
  });
  if (!entry || (uploadId && entry.uploadId !== uploadId)) return false;
  const key = entry.key;
  const record = records.get(key) || { ...entry, key };
  if (record.uploadId !== entry.uploadId) return false;

  const terminalRecord = {
    ...record,
    blob: null,
    locationBlob: null,
    state: "deleted",
    phase: null,
    updatedAt: Date.now(),
  };
  if (record.persisted !== false) {
    try {
      const persisted = await tombstoneDamageUploadRecord(terminalRecord);
      if (!persisted) return false;
    } catch (error) {
      console.error(
        "Schadenfoto-Löschung konnte nicht lokal bestätigt werden:",
        error
      );
      publishStatus({ persistenceAvailable: false });
      if (!serverConfirmed) return false;
    }
  }
  if (records.get(key)?.uploadId !== record.uploadId) return false;
  records.set(key, terminalRecord);
  releasePreviewUrl(key);
  removeEntry(key);
  return true;
}

export async function rollbackDamageUploadDelete({
  vehicleId,
  category = null,
  step,
  uploadId = null,
}) {
  const entry = getDamageUploadEntry(snapshot, {
    vehicleId,
    category,
    step,
  });
  if (!entry || (uploadId && entry.uploadId !== uploadId)) return false;
  const key = entry.key;
  const record = records.get(key) || { ...entry, key };
  if (record.uploadId !== entry.uploadId || record.state !== "deleting") {
    return false;
  }

  let restoredRecord;
  if (record.persisted !== false) {
    try {
      const restored = await restoreDamageUploadAfterDeleteFailure(
        key,
        record.uploadId
      );
      if (!restored.ok) {
        adoptStoredRecord(restored.current);
        return false;
      }
      restoredRecord = { ...restored.record, persisted: true };
    } catch (error) {
      console.error(
        "Schadenfoto-Löschung konnte nicht zurückgerollt werden:",
        error
      );
      publishStatus({ persistenceAvailable: false });
    }
  }
  if (!restoredRecord) {
    restoredRecord = { ...record };
    restoredRecord.state =
      record.stateBeforeDelete || (record.imageUrl ? "committed" : "pending");
    restoredRecord.phase =
      restoredRecord.state === "pending"
        ? record.phaseBeforeDelete || "queued"
        : null;
    delete restoredRecord.stateBeforeDelete;
    delete restoredRecord.phaseBeforeDelete;
  }
  if (records.get(key)?.uploadId !== record.uploadId) return false;
  records.set(key, restoredRecord);
  publishEntry(
    key,
    entryFromRecord(restoredRecord, snapshot[key]?.previewUrl || null)
  );
  if (restoredRecord.state === "pending") {
    const oldJob = jobs.get(key);
    if (oldJob) abortJob(oldJob);
    enqueueJob(createJob(restoredRecord));
  }
  return true;
}

export async function cancelDamageUpload({
  vehicleId,
  category = null,
  step,
}) {
  const prepared = await prepareDamageUploadDelete({
    vehicleId,
    category,
    step,
  });
  if (!prepared.found) return prepared;
  const confirmed = await confirmDamageUploadDelete({
    vehicleId,
    category: prepared.category,
    step,
    uploadId: prepared.expectedUploadId,
  });
  return { ...prepared, confirmed };
}

function isDamageResetEntryAffected(entry, vehicleId, categories) {
  if (String(entry?.vehicleId) !== String(vehicleId)) return false;
  if (!categories) return true;
  return categories.has(String(entry?.category || "").toLowerCase());
}

export async function prepareVehicleDamageUploadReset({
  vehicleId,
  categories = null,
}) {
  const categorySet = Array.isArray(categories)
    ? new Set(categories.map((category) => String(category).toLowerCase()))
    : null;
  const candidates = Object.values(snapshot).filter((entry) =>
    isDamageResetEntryAffected(entry, vehicleId, categorySet)
  );
  const preparedEntries = [];

  for (const entry of candidates) {
    const prepared = await prepareDamageUploadDelete({
      vehicleId,
      category: entry.category,
      step: entry.step,
      expectedImageUrl:
        entry.imageUrl || entry.previousImageUrl || entry.previewUrl || null,
    });
    if (prepared.conflict) {
      await rollbackVehicleDamageUploadReset({ preparedEntries });
      return {
        ok: false,
        conflict: true,
        preparedEntries: [],
      };
    }
    if (prepared.found) {
      preparedEntries.push({
        vehicleId,
        category: prepared.category,
        step: entry.step,
        uploadId: prepared.expectedUploadId,
      });
    }
  }

  return { ok: true, conflict: false, preparedEntries };
}

export async function confirmVehicleDamageUploadReset({
  preparedEntries = [],
}) {
  const results = await Promise.all(
    preparedEntries.map((entry) =>
      confirmDamageUploadDelete({
        vehicleId: entry.vehicleId,
        category: entry.category,
        step: entry.step,
        uploadId: entry.uploadId,
        serverConfirmed: true,
      })
    )
  );
  return results.filter(Boolean).length;
}

export async function rollbackVehicleDamageUploadReset({
  preparedEntries = [],
}) {
  const results = await Promise.all(
    preparedEntries.map((entry) =>
      rollbackDamageUploadDelete({
        vehicleId: entry.vehicleId,
        category: entry.category,
        step: entry.step,
        uploadId: entry.uploadId,
      })
    )
  );
  return results.filter(Boolean).length;
}

export function hasUnsafeVolatileDamageUploads(
  uploadSnapshot = snapshot
) {
  return Object.values(uploadSnapshot).some(
    (entry) =>
      entry?.hasLocalBlob === true &&
      ["pending", "failed"].includes(entry?.state)
  );
}

export async function awaitVehicleDamageUploads(vehicleId) {
  if (hydrationPromise) await hydrationPromise;
  if (!registryStatus.hydrated) {
    return {
      ok: false,
      failedEntries: [],
      pendingEntries: [
        {
          vehicleId,
          state: "pending",
          phase: registryStatus.hydrated
            ? "persistence_unavailable"
            : "hydrating",
        },
      ],
    };
  }

  if (currentOwnerEmail && !registryStatus.storageDefinitelyUnavailable) {
    try {
      const storedRecords = await getDamageUploadRecordsByOwner(
        currentOwnerEmail
      );
      storedRecords
        .filter((record) => String(record.vehicleId) === String(vehicleId))
        .forEach(adoptStoredRecord);
    } catch (error) {
      console.error(
        "Schadenfoto-Uploads konnten vor Abschluss nicht geprüft werden:",
        error
      );
      return {
        ok: false,
        failedEntries: [],
        pendingEntries: [
          { vehicleId, state: "pending", phase: "persistence_unavailable" },
        ],
      };
    }
  }

  const entries = Object.values(snapshot).filter(
    (entry) => String(entry?.vehicleId) === String(vehicleId)
  );
  const failedEntries = entries.filter((entry) => entry.state === "failed");
  const pendingEntries = entries.filter((entry) =>
    ["pending", "deleting"].includes(entry.state)
  );
  return {
    ok: failedEntries.length === 0 && pendingEntries.length === 0,
    failedEntries,
    pendingEntries,
  };
}

function resetRuntime(ownerEmail) {
  hydrationGeneration += 1;
  hydrationPromise = null;
  // Prevent abortJob from starting another queued upload while the owner is
  // being switched (for example during logout or a different-tab login).
  queue.length = 0;
  jobs.forEach((job) => abortJob(job));
  jobs.clear();
  retryTimers.forEach((timer) => clearTimeout(timer));
  retryTimers.clear();
  activeUploads = 0;
  ownedPreviewUrls.forEach((url) => globalThis.URL?.revokeObjectURL?.(url));
  ownedPreviewUrls.clear();
  records.clear();
  snapshot = Object.freeze({});
  currentOwnerEmail = ownerEmail;
  registryStatus = Object.freeze({
    hydrated: false,
    ownerEmail,
    persistenceAvailable: true,
    storageDefinitelyUnavailable: false,
  });
  emit();
}

async function backfillVolatileDamageRecords(
  ownerEmail,
  {
    persistRecord = putDamageUploadRecordIfCurrent,
    loadRecord = getDamageUploadRecord,
  } = {}
) {
  const volatileRecords = [...records.values()].filter(
    (record) =>
      record?.ownerEmail === ownerEmail && record.persisted === false
  );

  for (const record of volatileRecords) {
    const durableRecord = {
      ...record,
      persisted: true,
      updatedAt: Date.now(),
    };
    const persisted = await persistRecord(durableRecord);
    if (!persisted) {
      const storedRecord = await loadRecord(record.key);
      if (!storedRecord) {
        throw new Error(
          "Volatiler Schadenfoto-Upload konnte nicht dauerhaft gespeichert werden."
        );
      }
      adoptStoredRecord({ ...storedRecord, persisted: true });
      continue;
    }

    const current = records.get(record.key);
    if (current?.uploadId !== record.uploadId) continue;
    Object.assign(current, {
      persisted: true,
      updatedAt: durableRecord.updatedAt,
    });
    records.set(current.key, current);
    if (!HIDDEN_STATES.has(current.state)) {
      publishEntry(
        current.key,
        entryFromRecord(current, snapshot[current.key]?.previewUrl || null)
      );
    }
  }

  return volatileRecords.length;
}

export async function initializeDamageUploadRegistry({
  apiBaseUrl: nextApiBaseUrl,
  ownerEmail,
} = {}) {
  apiBaseUrl = String(nextApiBaseUrl || apiBaseUrl || "").replace(/\/+$/, "");
  const normalizedOwner = normalizeEmail(ownerEmail);

  if (normalizedOwner !== currentOwnerEmail) resetRuntime(normalizedOwner);
  if (
    registryStatus.hydrated &&
    registryStatus.persistenceAvailable &&
    registryStatus.ownerEmail === normalizedOwner
  ) {
    return registryStatus;
  }
  if (hydrationPromise) return hydrationPromise;

  publishStatus({ hydrated: false, ownerEmail: normalizedOwner });
  const generation = hydrationGeneration;
  hydrationPromise = (async () => {
    try {
      const storedRecords = normalizedOwner
        ? await getDamageUploadRecordsByOwner(normalizedOwner)
        : [];
      if (generation !== hydrationGeneration) return registryStatus;
      for (const storedRecord of storedRecords) {
        let hydratedRecord = storedRecord;
        if (storedRecord.state === "deleting") {
          const restored = await restoreDamageUploadAfterDeleteFailure(
            storedRecord.key,
            storedRecord.uploadId
          );
          hydratedRecord = restored.ok ? restored.record : restored.current;
        }
        if (!hydratedRecord) continue;
        adoptStoredRecord({ ...hydratedRecord, persisted: true });
      }
      // IndexedDB may recover after being temporarily blocked in Safari or in
      // a privacy mode. Only advertise persistence as available after every
      // in-memory volatile upload has been written back with CAS protection.
      await backfillVolatileDamageRecords(normalizedOwner);
      publishStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: true,
        storageDefinitelyUnavailable: false,
      });
    } catch (error) {
      if (generation !== hydrationGeneration) return registryStatus;
      console.error(
        "Gespeicherte Schadenfoto-Uploads konnten nicht geladen werden:",
        error
      );
      const readIsKnownEmpty =
        typeof globalThis.indexedDB === "undefined" ||
        ["SecurityError", "NotSupportedError"].includes(error?.name);
      publishStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: false,
        storageDefinitelyUnavailable:
          registryStatus.storageDefinitelyUnavailable || readIsKnownEmpty,
      });
    } finally {
      if (generation === hydrationGeneration) hydrationPromise = null;
    }

    if (!onlineListenerInstalled && typeof window !== "undefined") {
      onlineListenerInstalled = true;
      window.addEventListener("online", () => {
        jobs.forEach((job) => {
          if (!isJobCurrent(job)) return;
          clearRetryTimer(job.key);
          enqueueJob(job);
        });
      });
    }
    return registryStatus;
  })();

  return hydrationPromise;
}

export async function __resetDamageUploadRegistryForTests() {
  resetRuntime(null);
  registryStatus = EMPTY_STATUS;
  apiBaseUrl = "";
  uploadTimeoutOverrideMs = null;
  persistConfirmationRecord = putDamageUploadRecordIfCurrent;
}

export function __setDamageUploadTimeoutForTests(timeoutMs) {
  uploadTimeoutOverrideMs = Math.max(1, Number(timeoutMs) || 1);
}

export function __setDamageConfirmationPersistenceForTests(persist) {
  persistConfirmationRecord =
    typeof persist === "function"
      ? persist
      : putDamageUploadRecordIfCurrent;
}

export function __seedDamageUploadRecordForTests(record) {
  const seeded = {
    state: "pending",
    phase: "uploading",
    persisted: true,
    ...record,
  };
  records.set(seeded.key, seeded);
  publishEntry(seeded.key, entryFromRecord(seeded, seeded.previewUrl || null));
}

export async function __backfillVolatileDamageRecordsForTests(
  ownerEmail,
  adapters
) {
  return backfillVolatileDamageRecords(normalizeEmail(ownerEmail), adapters);
}
